import React, { useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import SvgMenuMessages from '@app/icons/menu/MenuMessages.tsx';
import MenuItem from '@components/menu/MenuItem.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { ApiContext } from '@api/api.tsx';
import { NotificationCenterMessageResponseOutDTO } from '@api/ringil-api.v2.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';
import { useLocalStorage } from '@utils/utils.tsx';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import useNotificationCenterNavigation from '@app/pages/notifications/useNotificationCenterNavigation.ts';

interface ComponentProps {
  /** Render as the icon rail. */
  smallMenu?: boolean;
}

const ITEMS_COUNT = 12;
const ITEM_HEIGHT = 64;
const MIN_VISIBLE_ITEMS = 4;

/**
 * Notification-center version of the menu badge: unread count from countNotificationCenterMessage,
 * the preview list from listNotificationCenterMessage. Which version renders is decided by the
 * NOTIFICATION_CENTER beta surface in {@link NotificationMenuItem}; this component has no switch of
 * its own — the user toggles it on the notifications page.
 */
const NotificationCenterMenuItem: React.FC<ComponentProps> = ({ smallMenu }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { listNotificationCenterMessage, countNotificationCenterMessage } = useContext(ApiContext);
  const nav = useNavigation();
  const { markAsReadAndNavigate } = useNotificationCenterNavigation();

  const [notifications, setNotifications] = useState<NotificationCenterMessageResponseOutDTO[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [notificationMode, setNotificationMode] = useLocalStorage('NOTIFICATION_FILTER', 'ALL');
  const [maxVisibleItems, setMaxVisibleItems] = useState<number>(ITEMS_COUNT);
  // The panel is open only while the pointer is over it (CSS :hover). Switching the mode to one with
  // fewer notifications would shrink it out from under the cursor and close it mid-click, so the list
  // keeps the tallest size it has had during this hover and only relaxes once the pointer leaves.
  const [stickyItemCount, setStickyItemCount] = useState<number>(0);

  const compute = () => {
    if (typeof window === 'undefined') return;
    const available = window.innerHeight - 3 * ITEM_HEIGHT;
    setMaxVisibleItems(Math.max(MIN_VISIBLE_ITEMS, Math.floor(available / ITEM_HEIGHT)) - 2);
  };

  useEffect(() => {
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationMode]);

  const load = () => {
    countNotificationCenterMessage(data => {
      setUnreadCount(data?.count ?? 0);
      if ((data?.count ?? 0) === 0) {
        setNotifications([]);
        return;
      }
      listNotificationCenterMessage(
        {
          isRead: false,
          // "All" must not send the flag at all — `false` would mean "system notifications only".
          ...(notificationMode === 'USER_MESSAGES' ? { isUserMessage: true } : {}),
        },
        { pageSize: ITEMS_COUNT, pageNumber: 1, sortField: 'createdAt', sortDirection: 'DESC' },
        page => {
          const data = page?.data ?? [];
          setNotifications(data);
          setStickyItemCount(prev => Math.max(prev, data.length));
          compute();
        },
      );
    });
  };

  const model = [
    { code: 'ALL', label: tr('Notification.all', 'all') },
    { code: 'USER_MESSAGES', label: tr('Notification.userMessages', 'User messages') },
  ];

  const content = (
    <div
      className={clsx('flex flex-col w-full')}
      style={{ maxHeight: `${(maxVisibleItems + 2) * ITEM_HEIGHT}px` }}
      onMouseLeave={() => setStickyItemCount(0)}
      onBlur={() => setStickyItemCount(0)}
    >
      <div className="w-full flex justify-center items-center my-3 h-16">
        <InputSelect model={model} selectedItemCode={notificationMode} onSelect={v => setNotificationMode(v)} />
      </div>
      <div className="flex flex-col flex-1 justify-between">
        <div
          className="flex flex-col border-0 border-t border-solid border-content-quaternary"
          style={{
            minHeight: `${Math.min(stickyItemCount, maxVisibleItems) * ITEM_HEIGHT}px`,
            maxHeight: `${maxVisibleItems * ITEM_HEIGHT}px`,
            overflowY: 'auto',
          }}
        >
          {notifications.length === 0 && <div className="px-4 py-6 text-center text-sm text-content-secondary">{tr('NotificationCenter.noUnread', 'No unread notifications')}</div>}
          {notifications.map(notification => (
            <li role="menuitem" key={notification.id} className="list-none border-0 border-b border-solid border-content-quaternary last:border-b-0">
              <button
                type="button"
                className="flex w-full cursor-pointer flex-col gap-1 border-none bg-transparent px-4 py-3 text-left hover:bg-base-base"
                onClick={() => markAsReadAndNavigate(notification, load)}
              >
                <span className="text-base font-medium text-content-primary">{notification.message}</span>
                <span className="flex w-full flex-row items-center justify-between gap-2 text-xs">
                  <span className="text-content-brand">{notification.applicationId}</span>
                  <span className="text-content-secondary">{stringDateTimeWithLocaleFromApiStringInTz(notification.createdAt, loggedUser?.defaultTimezoneId)}</span>
                </span>
              </button>
            </li>
          ))}
        </div>
        <div className="flex-col h-16 items-center">
          <MenuItem
            smallMenu={false}
            render={true}
            title={tr(`AppMenuNotification.seeAll`, `Vše`)}
            onClick={() => {
              nav.navigate(nav.urlFunctions.createCustomerNotifications());
            }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <MenuItem
      title={tr('NotificationMenuItem.messages', 'Messages')}
      icon={<SvgMenuMessages />}
      // The notification-center count endpoint returns a single unread total, with no
      // system/user split — so it goes to the plain count badge.
      elementCount={{ count: unreadCount }}
      smallMenu={smallMenu}
      render={true}
      mode={'open-outside-bottom'}
      subMenu={content}
    />
  );
};

export default NotificationCenterMenuItem;